/**
 * 经验推力估算。
 *
 * 仅在用户**没有填写实测最大推力**时使用，属于数量级参考，不作为适航依据。
 *
 * 模型（静推力经验式）：
 *   V_nominal = 电芯数 × 3.7 V
 *   RPM       = KV × V_nominal × 0.85        （带载转速折减，空载转速在负载下取不到）
 *   T[N]      = C_T × ρ × (RPM/60)² × D³ × P （D 为桨径、P 为螺距，单位米）
 *   T[g]      = T[N] / 9.80665 × 1000 × 桨叶数修正
 *
 * 校验样本（估算值 / 常见实测区间）：
 *   2300KV · 4S · 5×4.5   ≈ 750 g  / 800–1000 g
 *    920KV · 4S · 10×4.5  ≈ 970 g  / 1000–1200 g
 *    380KV · 6S · 15×5    ≈ 1390 g / 1500–1800 g
 * 整体略偏保守，因此界面上必须标注「估算值 ±20% 量级」。
 */

/** 静推力系数（经验值，适配常见多旋翼桨型） */
const THRUST_COEFFICIENT = 0.11;
/** 海平面标准空气密度 kg/m³ */
const AIR_DENSITY = 1.225;
/** 带载转速折减系数 */
const RPM_LOAD_FACTOR = 0.85;
/** 每片电芯的标称电压 V */
export const NOMINAL_CELL_VOLTAGE = 3.7;
const GRAVITY = 9.80665;

/** 桨叶数修正：以三叶桨为基准 */
function bladeFactor(blades: number | null): number {
  if (blades === null) return 1;
  if (blades <= 1) return 0.8;
  if (blades === 2) return 0.9;
  if (blades === 3) return 1;
  if (blades === 4) return 1.06;
  return 1.12;
}

export interface EstimateInputs {
  /** 电机 KV 值（实测或标称） */
  kv: number | null;
  /** 桨叶直径（英寸） */
  diameterIn: number | null;
  /** 桨叶螺距（英寸） */
  pitchIn: number | null;
  /** 电芯数（S） */
  cells: number | null;
  /** 桨叶数，留空按三叶桨处理 */
  blades?: number | null;
}

/** 估算缺少了哪些输入（用于「缺少参数“X”，无法估算」的口径说明） */
export function missingEstimateInputs(inputs: EstimateInputs): string[] {
  const missing: string[] = [];
  if (inputs.kv === null) missing.push('电机 KV');
  if (inputs.diameterIn === null) missing.push('桨叶直径');
  if (inputs.pitchIn === null) missing.push('桨叶螺距');
  if (inputs.cells === null) missing.push('电池电芯数');
  return missing;
}

/**
 * 单电机最大推力估算（克）。输入不足时返回 `null`，由调用方走缺失提示。
 */
export function estimateThrustPerMotor(inputs: EstimateInputs): number | null {
  if (missingEstimateInputs(inputs).length > 0) return null;

  const kv = inputs.kv as number;
  const diameterIn = inputs.diameterIn as number;
  const pitchIn = inputs.pitchIn as number;
  const cells = inputs.cells as number;

  if (kv <= 0 || diameterIn <= 0 || pitchIn <= 0 || cells <= 0) return null;

  const voltage = cells * NOMINAL_CELL_VOLTAGE;
  const rpm = kv * voltage * RPM_LOAD_FACTOR;
  const diameterM = diameterIn * 0.0254;
  const pitchM = pitchIn * 0.0254;

  const revolutionsPerSecond = rpm / 60;
  const thrustNewton =
    THRUST_COEFFICIENT *
    AIR_DENSITY *
    revolutionsPerSecond ** 2 *
    diameterM ** 3 *
    pitchM *
    bladeFactor(inputs.blades ?? null);

  return (thrustNewton / GRAVITY) * 1000;
}
